import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { Category } from '../types/category.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_CATEGORIES_API_PATHS } from '../constants/categories-api-paths.constants';

export interface GetCategoriesParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  filters?: {
    isActive?: boolean;
    archived?: boolean;
  };
}

type UseAdminGetCategoriesResult = {
  isLoading: boolean;
  isRefetching: boolean;
  categories: Category[];
  totalCount: number;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetCategoriesResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  categories: [],
  totalCount: 0,
};

export function useAdminGetCategories(params?: GetCategoriesParams): UseAdminGetCategoriesResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const limit = params?.limit ?? 1000;
  const sortBy = params?.sortBy ?? 'createdAt';
  const sortDirection = params?.sortDirection ?? 'asc';
  const page = params?.page;
  const serializedFilters = JSON.stringify({
    isActive: params?.filters?.isActive,
    archived: params?.filters?.archived,
  });

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<{ totalCount: number; categories: Category[] }>(
        ADMIN_CATEGORIES_API_PATHS.GET_CATEGORIES,
        { params: { page, limit, sortBy, sortDirection, filters: serializedFilters } }
      );

      setResult({
        isLoading: false,
        isRefetching: false,
        categories: response.categories,
        totalCount: response.totalCount,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des rubriques'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, categories: [], totalCount: 0 });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
